import './style.css'
import { AuthForm, AuthFormData } from './components/AuthForm'
import { InvoicesList, Invoice, InvoiceItem } from './components/InvoicesList'
import { ReportsExplorer } from './components/ReportsExplorer'
import pb, { login, register, isAuthenticated, getCurrentUser, onAuthChange, logout, User } from './lib/pocketbase'
import { ProcessInvoiceImage } from './services/ProcessInvoiceImage'
import { ScannerOverlay } from './scanner/ScannerOverlay'

interface Store {
  id: string
  name?: string
}

type AppUser = User & { role?: string }

class App {
  private app: HTMLElement
  private currentUser: AppUser | null = null
  private authForm: AuthForm | null = null
  private invoicesList: InvoicesList | null = null
  private reportsExplorer: ReportsExplorer | null = null
  private currentReportFilter: { year: number; month: number; label: string } | null = null
  private availableStores: Store[] = []
  private currentStoreId: string | null = null
  private isLoadingStores = false
  private handleInvoicesDataChange = () => {
    if (this.reportsExplorer) {
      void this.reportsExplorer.refresh()
    }
  }

  constructor() {
    this.app = document.querySelector<HTMLDivElement>('#app')!
    this.init()
  }

  private async init(): Promise<void> {
    // Verificar si el usuario ya está autenticado
    if (isAuthenticated()) {
      this.currentUser = getCurrentUser() as AppUser | null
      await this.renderDashboard()
    } else {
      this.renderAuth()
    }

    // Escuchar cambios en el estado de autenticación
    onAuthChange((user) => {
      this.currentUser = user as AppUser | null
      this.currentReportFilter = null
      this.currentStoreId = null
      this.availableStores = []
      this.invoicesList = null
      this.reportsExplorer = null
      if (user) {
        void this.renderDashboard()
      } else {
        this.renderAuth()
      }
    })
  }

  private renderAuth(): void {
    this.app.innerHTML = `
      <div class="auth-container">
        <div class="auth-wrapper">
          <div class="auth-logo">
            <h1>📄 Invoice Scanner</h1>
            <p>Gestiona y escanea tus facturas de manera inteligente</p>
          </div>
          <div id="auth-form-container"></div>
        </div>
      </div>
    `

    const formContainer = document.querySelector<HTMLElement>('#auth-form-container')!
    this.authForm = new AuthForm(formContainer, {
      mode: 'login',
      onSubmit: this.handleAuthSubmit.bind(this),
      isLoading: false,
      error: null
    })
  }

  private async renderDashboard(): Promise<void> {
    if (!this.currentUser) return
    const isAccountant = this.isAccountant()

    this.app.innerHTML = `
      <div class="dashboard">
        <header class="dashboard-header">
          <div class="dashboard-header-left">
            <div class="user-info">
              <h2>¡Bienvenido, ${this.currentUser.name}!</h2>
              <p>${this.currentUser.email}</p>
            </div>
          </div>
          <div id="store-selector-container" class="dashboard-header-center"></div>
          <div class="dashboard-header-right">
            <button id="logout-btn" class="logout-btn">Cerrar Sesión</button>
          </div>
        </header>
        
        <main class="dashboard-content">
          <div class="welcome-card">
            <h3>Panel de Control</h3>
            <p>Aquí podrás gestionar tus facturas y utilizar las funciones de escaneo.</p>
            
            <div class="feature-grid">
              <div class="feature-card">
                <h4>📸 Escanear Factura</h4>
                <p>Sube una imagen de tu factura para extraer información automáticamente</p>
                <button id="scan-btn" class="feature-btn">Escanear Ahora</button>
              </div>
              
              <div class="feature-card">
                <h4>📋 Ver Facturas</h4>
                <p>Revisa todas tus facturas escaneadas y organizadas</p>
                <button id="view-invoices-btn" class="feature-btn">Ver Lista</button>
              </div>
              
              <div class="feature-card">
                <h4>📊 Reportes</h4>
                <p>Genera reportes y análisis de tus gastos</p>
                <button id="view-reports-btn" class="feature-btn">Ver Reportes</button>
              </div>
            </div>
          </div>
          
          <div id="reports-explorer-container"></div>
          <div id="invoices-list-container"></div>
        </main>
      </div>
    `

    // Event listeners
    const logoutBtn = document.querySelector('#logout-btn')
    if (logoutBtn) {
      logoutBtn.addEventListener('click', () => {
        this.handleLogout()
      })
    }

    const scanBtn = document.querySelector('#scan-btn')
    if (scanBtn) {
      scanBtn.addEventListener('click', () => {
        this.scan()
      })
    }

    const viewInvoicesBtn = document.querySelector('#view-invoices-btn')
    if (viewInvoicesBtn) {
      viewInvoicesBtn.addEventListener('click', () => {
        this.currentReportFilter = null
        this.invoicesList?.setFilters(null)
        this.reportsExplorer?.setActiveGroup(0, null)
      })
    }

    const viewReportsBtn = document.querySelector('#view-reports-btn')
    if (viewReportsBtn) {
      viewReportsBtn.addEventListener('click', () => {
        const reportsContainer = document.querySelector('#reports-explorer-container')
        if (reportsContainer) {
          reportsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    }

    if (isAccountant) {
      await this.setupAccountantDashboard()
    } else {
      this.currentStoreId = this.currentUser.storeId
      this.showReportsExplorer()
      this.showInvoicesList()
    }
  }

  private isAccountant(): boolean {
    return this.currentUser?.role === 'ACCOUNTANT'
  }

  private async setupAccountantDashboard(): Promise<void> {
    const selectorContainer = document.querySelector<HTMLElement>('#store-selector-container')
    if (!selectorContainer) return

    selectorContainer.innerHTML = `
      <div class="store-selector-loading">⏳ Cargando tiendas...</div>
    `

    try {
      if (!this.availableStores.length && !this.isLoadingStores) {
        this.isLoadingStores = true
        const records = await pb.collection('stores').getFullList({ sort: 'name' })
        this.availableStores = records as unknown as Store[]
      }

      if (!this.availableStores.length) {
        selectorContainer.innerHTML = `
          <div class="store-selector-empty">
            <p>📂 No hay tiendas disponibles.</p>
          </div>
        `
        return
      }

      if (!this.currentStoreId) {
        const preferredStoreId = this.currentUser?.storeId
        const preferredStoreExists = preferredStoreId
          ? this.availableStores.some((store) => store.id === preferredStoreId)
          : false
        this.currentStoreId = preferredStoreExists
          ? preferredStoreId!
          : this.availableStores[0].id
      }

      selectorContainer.innerHTML = this.renderStoreSelectorHtml()
      this.attachStoreSelectorListener()

      await this.refreshDataForStore()
    } catch (error) {
      console.error('Error al cargar tiendas:', error)
      selectorContainer.innerHTML = `
        <div class="store-selector-error">
          <p>❌ Error al cargar tiendas.</p>
        </div>
      `
    } finally {
      this.isLoadingStores = false
    }
  }

  private renderStoreSelectorHtml(): string {
    const options = this.availableStores
      .map((store) => {
        const label = this.escapeHtml(store.name ?? store.id)
        const isSelected = store.id === this.currentStoreId ? 'selected' : ''
        return `<option value="${store.id}" ${isSelected}>${label}</option>`
      })
      .join('')

    return `
      <div class="store-selector">
        <label for="store-selector" class="store-selector-label">Tienda</label>
        <select id="store-selector" class="store-selector-select">
          ${options}
        </select>
      </div>
    `
  }

  private attachStoreSelectorListener(): void {
    const selector = document.querySelector<HTMLSelectElement>('#store-selector')
    if (!selector) return

    selector.addEventListener('change', (event) => {
      const newStoreId = (event.target as HTMLSelectElement).value
      if (!newStoreId || newStoreId === this.currentStoreId) {
        return
      }

      this.currentStoreId = newStoreId
      void this.refreshDataForStore()
    })
  }

  private async refreshDataForStore(): Promise<void> {
    const activeStoreId = this.getActiveStoreId()
    if (!activeStoreId || !this.currentUser) {
      return
    }

    this.currentReportFilter = null

    if (this.reportsExplorer) {
      await this.reportsExplorer.updateStore(activeStoreId)
    } else {
      this.showReportsExplorer()
    }

    if (this.invoicesList) {
      this.invoicesList.updateProps({ storeId: activeStoreId })
    } else {
      this.showInvoicesList()
    }
  }

  private getActiveStoreId(): string | null {
    if (this.currentStoreId) {
      return this.currentStoreId
    }

    if (this.currentUser?.storeId) {
      return this.currentUser.storeId
    }

    return null
  }

  private showReportsExplorer(): void {
    if (!this.currentUser) return

    const container = document.querySelector<HTMLElement>('#reports-explorer-container')
    if (!container) return

    const storeId = this.getActiveStoreId()
    if (!storeId) return

    this.reportsExplorer = new ReportsExplorer(container, {
      storeId,
      onGroupSelect: ({ year, month, label }) => {
        this.currentReportFilter = { year, month, label }
        this.invoicesList?.setFilters({ year, month })
        container.scrollIntoView({ behavior: 'smooth', block: 'start' })
      },
      onClearSelection: () => {
        this.currentReportFilter = null
        this.invoicesList?.setFilters(null)
      }
    })
  }

  private showInvoicesList(): void {
    if (!this.currentUser) return

    const container = document.querySelector<HTMLElement>('#invoices-list-container')
    if (!container) return

    const storeId = this.getActiveStoreId() ?? undefined

    this.invoicesList = new InvoicesList(container, {
      userId: this.currentUser.id,
      storeId,
      onInvoiceClick: (invoice: Invoice) => {
        this.showInvoiceDetail(invoice)
      },
      onDataChange: this.handleInvoicesDataChange
    })

    if (this.currentReportFilter) {
      this.invoicesList.setFilters({
        year: this.currentReportFilter.year,
        month: this.currentReportFilter.month
      })
    }
  }

  private async showInvoiceDetail(invoice: Invoice): Promise<void> {
    // Crear HTML para la tabla de items
    let itemsHTML = '';
    if (invoice.items && invoice.items.length > 0) {
      const itemsRows = invoice.items.map((item: InvoiceItem) => `
        <tr class="invoice-detail-item-row">
          <td>${this.escapeHtml(item.ref)}</td>
          <td>${this.escapeHtml(item.name)}</td>
          <td style="text-align: center;">${item.quantity}</td>
          <td style="text-align: right;">${this.formatCurrency(item.unitPrice)}</td>
          <td style="text-align: right; font-weight: 600;">${this.formatCurrency(item.total)}</td>
        </tr>
      `).join('');

      itemsHTML = `
        <div style="margin-top: 2rem;">
          <h4 style="margin: 0 0 1rem 0; color: var(--text-primary); font-size: 1.1rem;">📋 Items de la Factura</h4>
          <div class="invoice-items-table-wrapper">
            <table class="invoice-items-table">
              <thead>
                <tr>
                  <th>Ref.</th>
                  <th>Descripción</th>
                  <th style="text-align: center;">Cant.</th>
                  <th style="text-align: right;">Precio Unit.</th>
                  <th style="text-align: right;">Total</th>
                </tr>
              </thead>
              <tbody>
                ${itemsRows}
              </tbody>
            </table>
          </div>
        </div>
      `;
    }

    let imageHTML = '';
    if (invoice.imgUrl) {
      try {
        const presignedImageUrl = await this.presignedUrl(invoice.imgUrl)
        imageHTML = `
          <div style="margin-top: 2rem;">
            <h4 style="margin: 0 0 1rem 0; color: var(--text-primary); font-size: 1.1rem;">🖼️ Imagen de la Factura</h4>
            <div style="background: var(--bg-primary); border-radius: 12px; padding: 1rem; border: 1px solid var(--border-color); display: flex; justify-content: center;">
              <img src="${presignedImageUrl}" alt="Imagen de la factura" style="max-width: 100%; max-height: 60vh; border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.1); object-fit: contain;" />
            </div>
            <a href="${presignedImageUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-block; margin-top: 0.75rem; color: var(--primary-color); text-decoration: none; font-weight: 600;">Abrir en nueva pestaña ↗</a>
          </div>
        `;
      } catch (error) {
        console.error('Error al obtener la URL firmada:', error)
        imageHTML = `
          <div style="margin-top: 2rem;">
            <p style="color: var(--error-color);">No fue posible cargar la imagen de la factura.</p>
          </div>
        `;
      }
    }

    // Crear modal para mostrar el detalle de la factura
    const modal = document.createElement('div')
    modal.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                  background: rgba(0,0,0,0.5); z-index: 1000; display: flex; 
                  align-items: center; justify-content: center; padding: 1rem;">
        <div style="background: var(--bg-secondary); padding: 2rem; border-radius: 16px; 
                    max-width: 800px; width: 95%; max-height: 90%; overflow-y: auto;
                    border: 1px solid var(--border-color);">
          <h3 style="margin: 0 0 1.5rem 0; color: var(--text-primary);">📄 Detalle de Factura</h3>
          
          <div style="display: flex; flex-direction: column; gap: 1rem;">
            <div>
              <strong style="color: var(--text-secondary);">Proveedor:</strong>
              <p style="margin: 0.25rem 0 0 0; color: var(--text-primary); font-size: 1.1rem;">${this.escapeHtml(invoice.vendor)}</p>
            </div>
            
            <div>
              <strong style="color: var(--text-secondary);">Fecha:</strong>
              <p style="margin: 0.25rem 0 0 0; color: var(--text-primary);">${this.formatDate(invoice.date)}</p>
            </div>
            
            <div>
              <strong style="color: var(--text-secondary);">Total:</strong>
              <p style="margin: 0.25rem 0 0 0; color: var(--success-color); font-size: 1.25rem; font-weight: 600;">${this.formatCurrency(invoice.total)}</p>
            </div>
            
            <div>
              <strong style="color: var(--text-secondary);">ID:</strong>
              <p style="margin: 0.25rem 0 0 0; color: var(--text-primary); font-size: 0.9rem; font-family: monospace;">${invoice.id}</p>
            </div>
            
            <div>
              <strong style="color: var(--text-secondary);">Creada:</strong>
              <p style="margin: 0.25rem 0 0 0; color: var(--text-primary); font-size: 0.9rem;">${this.formatDate(invoice.created)}</p>
            </div>
          </div>
          
          ${itemsHTML}
          ${imageHTML}
          
          <button id="close-detail-modal" 
                  style="background: var(--primary-color); color: white; border: none; 
                         padding: 0.75rem 1.5rem; border-radius: 8px; cursor: pointer;
                         margin-top: 1.5rem; width: 100%; font-size: 1rem;">
            Cerrar
          </button>
        </div>
      </div>
    `

    document.body.appendChild(modal)

    const closeBtn = modal.querySelector('#close-detail-modal')
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        document.body.removeChild(modal)
      })
    }

    // Cerrar al hacer clic fuera del modal
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal)
      }
    })
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch {
      return dateString
    }
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  private getApiBaseUrl(): string {
    // Verificar si existe la configuración global (generada dinámicamente en Docker)
    if (typeof window !== 'undefined' && (window as any).APP_CONFIG) {
      const config = (window as any).APP_CONFIG
      return config.API_BASE_URL || config.VITE_POCKETBASE_URL || 'http://localhost:8089'
    }

    // Fallback a variables de entorno de Vite (desarrollo)
    return import.meta.env.VITE_POCKETBASE_URL || 'http://localhost:8089'
  }

  private async presignedUrl(imagePath: string): Promise<string> {
    imagePath = imagePath.replace(/^[^/]*\//, "");
    const encodedPath = encodeURIComponent(imagePath)
    const response = await fetch(`${this.getApiBaseUrl()}/api/media/${encodedPath}`)
    if (!response.ok) {
      throw new Error(`Error al obtener URL firmada: ${response.statusText}`)
    }

    const data = await response.json()
    if (!data?.url) {
      throw new Error('Respuesta inválida al solicitar URL firmada')
    }

    return data.url
  }

  private async handleAuthSubmit(data: AuthFormData): Promise<void> {
    if (!this.authForm) return

    // Actualizar estado de carga
    this.authForm.updateProps({ isLoading: true, error: null })

    try {
      if (data.name) {
        // Registro
        await register(data.email, data.password, data.name)
      } else {
        // Login
        await login(data.email, data.password)
      }
    } catch (error: any) {
      console.error('Error de autenticación:', error)

      let errorMessage = 'Error de autenticación'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      this.authForm.updateProps({
        isLoading: false,
        error: errorMessage
      })
    }
  }

  private handleLogout(): void {
    logout()
  }

  private async scan(): Promise<void> {
    if (!this.currentUser) {
      alert('No hay un usuario logueado')
      return
    }
    const activeStoreId = this.getActiveStoreId()
    if (!activeStoreId) {
      alert('Selecciona una tienda válida antes de subir facturas.')
      return
    }

    // Abrir overlay de escáner en tiempo real con corners y UI propia
    new ScannerOverlay(document.body, {
      onCapture: async (file) => {
        // Crear pop-up de previsualización de la captura
        const previewUrl = URL.createObjectURL(file)
        const previewModal = document.createElement('div')
        previewModal.innerHTML = `
          <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.65); z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 1rem;">
            <div style="background: var(--bg-secondary, #111827); border-radius: 16px; padding: 1.5rem; max-width: 480px; width: 100%; box-shadow: 0 20px 40px rgba(0,0,0,0.4); border: 1px solid rgba(148,163,184,0.4); color: var(--text-primary, #f9fafb);">
              <h3 style="margin: 0 0 0.75rem 0; font-size: 1.1rem;">📸 Previsualizar captura</h3>
              <p style="margin: 0 0 1rem 0; font-size: 0.9rem; color: var(--text-secondary, #9ca3af);">
                Revisa la imagen antes de procesarla como factura.
              </p>
              <div style="background: #000; border-radius: 12px; padding: 0.5rem; max-height: 55vh; display: flex; align-items: center; justify-content: center; margin-bottom: 1rem;">
                <img src="${previewUrl}" alt="Captura de factura" style="max-width: 100%; max-height: 50vh; object-fit: contain; border-radius: 8px;" />
              </div>
              <div style="display: flex; gap: 0.75rem; margin-top: 0.5rem;">
                <button id="scan-preview-cancel" style="flex: 1; padding: 0.6rem 1rem; border-radius: 999px; border: 1px solid var(--border-color, #4b5563); background: transparent; color: var(--text-primary, #f9fafb); cursor: pointer; font-size: 0.95rem;">
                  Repetir captura
                </button>
                <button id="scan-preview-confirm" style="flex: 1; padding: 0.6rem 1rem; border-radius: 999px; border: none; background: #22c55e; color: #022c22; cursor: pointer; font-size: 0.95rem; font-weight: 600; box-shadow: 0 10px 25px rgba(22,163,74,0.5);">
                  Usar esta imagen
                </button>
              </div>
            </div>
          </div>
        `
        document.body.appendChild(previewModal)

        const closePreview = () => {
          URL.revokeObjectURL(previewUrl)
          if (previewModal.parentElement) {
            previewModal.parentElement.removeChild(previewModal)
          }
        }

        const cancelBtn = previewModal.querySelector<HTMLButtonElement>('#scan-preview-cancel')
        const confirmBtn = previewModal.querySelector<HTMLButtonElement>('#scan-preview-confirm')

        cancelBtn?.addEventListener('click', () => {
          // Cerrar el modal y reabrir inmediatamente el escáner para repetir la captura
          closePreview()
          void this.scan()
        })

        confirmBtn?.addEventListener('click', async () => {
          closePreview()

          const loadingProcessing = document.createElement('div')
          loadingProcessing.innerHTML = `
            <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
                        background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                        z-index: 10000; text-align: center;">
              <p>🔄 Procesando documento...</p>
            </div>
          `
          document.body.appendChild(loadingProcessing)

          try {
            const processedFile = await ProcessInvoiceImage.run(file)
            await this.uploadFile(processedFile)
          } catch (error) {
            console.error('Error al procesar documento escaneado:', error)
            alert('Error al procesar el documento. Por favor, inténtalo de nuevo.')
          } finally {
            if (loadingProcessing.parentElement) {
              loadingProcessing.parentElement.removeChild(loadingProcessing)
            }
          }
        })
      }
    })
  }

  private async uploadFile(file: File): Promise<void> {
    // Verificar que hay un usuario logueado
    if (!this.currentUser) {
      alert('No hay un usuario logueado')
      return
    }

    const activeStoreId = this.getActiveStoreId()
    if (!activeStoreId) {
      alert('Selecciona una tienda válida antes de subir facturas.')
      return
    }

    try {
      // Crear FormData para enviar el archivo y el ID del usuario
      const formData = new FormData()
      formData.append('file', file)
      formData.append('userId', this.currentUser.id)
      formData.append('storeId', activeStoreId)

      console.log("currentUser", this.currentUser)
      console.log("storeId", activeStoreId)

      // Enviar archivo al servidor
      const apiBaseUrl = this.getApiBaseUrl()
      const response = await fetch(`${apiBaseUrl}/api/invoice/scan`, {
        method: 'POST',
        body: formData,
        headers: {
          // No establecer Content-Type, fetch lo manejará automáticamente para FormData
        }
      })

      if (!response.ok) {
        throw new Error(`Error del servidor: ${response.status}`)
      }

      const result = await response.json()
      console.log('Respuesta del servidor:', result)

      // Mostrar resultado
      this.showScanResult(result)

      // Refrescar la lista de facturas si está visible
      if (this.invoicesList) {
        await this.invoicesList.refresh()
      }

    } catch (error: any) {
      console.error('Error al subir archivo:', error)
      alert(`Error al procesar el archivo: ${error.message}`)
    }
  }

  private showScanResult(result: any): void {
    // Crear modal para mostrar resultado
    const modal = document.createElement('div')
    modal.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                  background: rgba(0,0,0,0.5); z-index: 1000; display: flex; 
                  align-items: center; justify-content: center;">
        <div style="background: white; padding: 20px; border-radius: 8px; 
                    max-width: 600px; max-height: 80%; overflow-y: auto;">
          <h3>✅ Factura Escaneada</h3>
          <div style="margin: 15px 0;">
            <strong>ID:</strong> ${result.id || 'N/A'}<br>
          </div>
          <div style="margin: 15px 0;">
            <strong>Datos extraídos:</strong>
            <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; 
                       overflow-x: auto; font-size: 12px;">
${JSON.stringify(result.parsed || result, null, 2)}
            </pre>
          </div>
          <button onclick="this.parentElement.parentElement.remove()" 
                  style="background: #007bff; color: white; border: none; 
                         padding: 10px 20px; border-radius: 4px; cursor: pointer;">
            Cerrar
          </button>
        </div>
      </div>
    `

    document.body.appendChild(modal)
  }
}

// Inicializar la aplicación
new App()
